import { EXTENSION_ID } from '@/lib/env'

/** DOM marker the extension's content script appends to <html> as its very
 *  first act (see extension/utils/contentScriptUtils.ts `addContentScriptMarker`).
 *  It lands in the same synchronous block that registers the postMessage
 *  handlers, so its presence means the handshake below will be answered. */
const CONTENT_SCRIPT_MARKER_ID = 'verse-content-script-marker'

/** Synchronous, race-free presence check. Absence is inconclusive (an older
 *  extension build may not set the marker), so callers fall back to the ping. */
function hasContentScriptMarker(): boolean {
  if (typeof document === 'undefined') return false
  return (
    document.getElementById(CONTENT_SCRIPT_MARKER_ID)?.getAttribute(
      'data-verse-ready'
    ) === 'true'
  )
}

/**
 * Check if the browser extension is installed
 * Sends a message to the extension and waits for acknowledgment.
 * @param timeout - Timeout in milliseconds (default: 1000ms)
 * @returns Promise<boolean> - true if extension is installed, false otherwise
 */
export async function isExtensionInstalled(timeout: number = 1000): Promise<boolean> {
  if (hasContentScriptMarker()) return true

  return new Promise((resolve) => {
    let acknowledged = false;

    function handleExtensionAck(event: MessageEvent) {
      if (event.source !== window) return;
      if (event.data?.type === 'EXTENSION_ACK') {
        acknowledged = true;
        window.removeEventListener('message', handleExtensionAck);
        resolve(true);
      }
    }

    window.addEventListener('message', handleExtensionAck);

    // Send ping to extension
    window.postMessage({ type: 'OPEN_SCREENATA_EXTENSION' }, window.location.origin);

    // Timeout
    setTimeout(() => {
      window.removeEventListener('message', handleExtensionAck);
      resolve(acknowledged);
    }, timeout);
  });
}

/**
 * Open extension popup or redirect to extension store
 * Now uses handleOpenExtension internally for consistency
 */
export async function openExtensionOrRedirect({
  topicId,
  workspaceId,
  sectionId,
  templateId,
  controlId,
  taskId,
}: {
  topicId?: string;
  workspaceId?: string;
  sectionId?: string;
  templateId?: string;
  controlId?: string;
  taskId?: string;
} = {}) {
  // Use the same implementation as handleOpenExtension
  await handleOpenExtension(topicId, workspaceId, sectionId, templateId, controlId, taskId);
}

/**
 * Ask the extension to open its side panel directly.
 */
export const sendMessageToExtension = async (
  topicId?: string,
  workspaceId?: string,
  sectionId?: string,
  templateId?: string,
  controlId?: string,
  taskId?: string
): Promise<void> => {
  return new Promise((resolve, reject) => {
    let acknowledged = false;

    const handleExtensionAck = (event: MessageEvent) => {
      if (event.source !== window) return;
      if (event.data?.type === 'EXTENSION_ACK') {
        acknowledged = true;
        window.removeEventListener('message', handleExtensionAck);
        resolve();
      }
    };

    window.addEventListener('message', handleExtensionAck);

    window.postMessage(
      {
        type: 'OPEN_SCREENATA_INTERFACE',
        topicId: topicId,
        workspaceId: workspaceId,
        sectionId: sectionId,
        templateId: templateId,
        controlId: controlId,
        taskId: taskId,
      },
      window.location.origin
    );

    setTimeout(() => {
      window.removeEventListener('message', handleExtensionAck);
      if (!acknowledged) {
        reject(new Error('Extension not responding or not installed'));
      }
    }, 1000);
  });
};

/**
 * 处理打开扩展程序的主要逻辑
 */
export const handleOpenExtension = async (
  topicId?: string,
  workspaceId?: string,
  sectionId?: string,
  templateId?: string,
  controlId?: string,
  taskId?: string
) => {
  try {
    console.log('✅ Sending message to extension to open side panel...', { topicId, workspaceId, sectionId, templateId, controlId, taskId });
    await sendMessageToExtension(topicId, workspaceId, sectionId, templateId, controlId, taskId);
    console.log('✅ Message sent successfully, extension should open side panel');
  } catch (error) {
    console.error('❌ Failed to send message to extension:', error);
    console.log('Opening extension download page...');
    window.open(
      `https://chromewebstore.google.com/detail/deepguide-recorder-screen/${EXTENSION_ID}`
    );
  }
};

export const CHROME_WEB_STORE_URL = `https://chromewebstore.google.com/detail/deepguide-recorder-screen/${EXTENSION_ID}`

export type BrowserCaptureGuidance = {
  rationale: string
  entryUrl?: string
  frames: Array<{
    label: string
    instructions: string
    annotations?: string[]
  }>
}

export type BrowserRemedyGuidance = {
  rationale: string
  entryUrl: string
  riskWarning: string
  steps: Array<{
    label: string
    instructions: string
    annotations?: string[]
  }>
}

interface StartCaptureBase {
  /** Organization the workspace belongs to. The extension may be active
   *  in a different org; passing this lets the extension switch first so
   *  the queued task fetch hits the right tenant. */
  organizationId?: string
  workspaceId: string
  /** Existing test (ControlEvidenceStatus) — captures attach to this test, picker is skipped. */
  taskId?: string
  /** Procedure-run StepRun to bind the recording to (human `browser_recording`
   *  field). Mutually exclusive with `taskId` — when set, the recording becomes
   *  run-scoped step evidence instead of a test/CES submission. */
  stepRunId?: string
  /** Human-form field id the recording satisfies (paired with `stepRunId`). */
  stepFieldId?: string
  /** Pre-selected ControlDef ids — picker pre-checks these but lets user adjust. */
  controlDefIds?: string[]
  /** Optional page to open before the extension shows the queued capture flow. */
  targetUrl?: string
  agentGuidance?: BrowserCaptureGuidance
  /** Human-readable label for what this capture is attached to (e.g.
   *  "HA-2 — Risk Management Plan"). Shown as a banner inside the side
   *  panel so the user knows what their next click is going to record. */
  contextLabel?: string
  /** Surface label sent through to telemetry. */
  source?: string
  completionMode?: 'evidence' | 'remedy'
  timeoutMs?: number
}

export type BrowserCaptureFallbackReason =
  | 'chrome-too-old'
  | 'no-tab-id'
  | 'mode-disabled'
  | 'gesture-lost'
  | 'window-not-focused'
  | 'window-open-failed'
  | 'unknown'

/**
 * Ask the extension to start a compliance recording. Resolves with
 * `started: true` when the extension acks; resolves with `reason: 'no-ack'`
 * after `timeoutMs` (extension missing OR content script not yet injected
 * on this tab — both require the user to install/reload).
 */
export async function startBrowserCapture({
  organizationId,
  workspaceId,
  taskId,
  stepRunId,
  stepFieldId,
  controlDefIds,
  targetUrl,
  agentGuidance,
  contextLabel,
  source = 'portal',
  completionMode,
  timeoutMs = 1500,
}: StartCaptureBase): Promise<{
  started: boolean
  reason?: 'no-ack' | 'failed'
  /** True when the extension auto-opened the side panel — lets the
   *  caller skip the "click the extension icon" instruction in the toast. */
  sidepanelOpened?: boolean
  fallbackReason?: BrowserCaptureFallbackReason
  fallbackMessage?: string
  /** The extension background's error string on a hard `reason: 'failed'`
   *  (e.g. "Failed to switch organization"). Surfaced verbatim so the
   *  caller's toast is actionable instead of generic. */
  error?: string
}> {
  return new Promise((resolve) => {
    let settled = false
    const finish = (result: {
      started: boolean
      reason?: 'no-ack' | 'failed'
      sidepanelOpened?: boolean
      fallbackReason?: BrowserCaptureFallbackReason
      fallbackMessage?: string
      error?: string
    }) => {
      if (settled) return
      settled = true
      window.removeEventListener('message', handleAck)
      resolve(result)
    }

    const handleAck = (event: MessageEvent) => {
      if (event.source !== window) return
      if (event.data?.type === 'SCREENATA_RECORDING_STARTED') {
        finish({
          started: true,
          sidepanelOpened: !!event.data?.sidepanelOpened,
          fallbackReason: event.data?.fallbackReason,
          fallbackMessage: event.data?.fallbackMessage,
        })
      } else if (event.data?.type === 'SCREENATA_RECORDING_FAILED') {
        finish({
          started: false,
          reason: 'failed',
          fallbackReason: event.data?.fallbackReason,
          fallbackMessage: event.data?.fallbackMessage,
          error: event.data?.error,
        })
      }
    }

    window.addEventListener('message', handleAck)
    window.postMessage(
      {
        type: 'SCREENATA_START_RECORDING',
        organizationId,
        workspaceId,
        taskId,
        stepRunId,
        stepFieldId,
        controlDefIds,
        targetUrl,
        agentGuidance,
        contextLabel,
        recordingMode: 'compliance',
        completionMode,
        source,
      },
      window.location.origin
    )

    setTimeout(() => finish({ started: false, reason: 'no-ack' }), timeoutMs)
  })
}

/** @deprecated Use `startBrowserCapture` directly. */
export const startBrowserCaptureForTest = startBrowserCapture

interface StartRemedyBase {
  organizationId?: string
  workspaceId: string
  taskId?: string
  remediationTicketId?: string
  targetUrl: string
  agentGuidance: BrowserCaptureGuidance
  contextLabel?: string
  source?: string
  timeoutMs?: number
}

export async function startBrowserRemedy({
  organizationId,
  workspaceId,
  taskId,
  remediationTicketId,
  targetUrl,
  agentGuidance,
  contextLabel,
  source = 'portal',
  timeoutMs = 1500,
}: StartRemedyBase): Promise<{
  started: boolean
  reason?: 'no-ack' | 'failed'
  sidepanelOpened?: boolean
  fallbackReason?: BrowserCaptureFallbackReason
  fallbackMessage?: string
  error?: string
}> {
  return new Promise((resolve) => {
    let settled = false
    const finish = (result: {
      started: boolean
      reason?: 'no-ack' | 'failed'
      sidepanelOpened?: boolean
      fallbackReason?: BrowserCaptureFallbackReason
      fallbackMessage?: string
      error?: string
    }) => {
      if (settled) return
      settled = true
      window.removeEventListener('message', handleAck)
      resolve(result)
    }

    const handleAck = (event: MessageEvent) => {
      if (event.source !== window) return
      if (event.data?.type === 'SCREENATA_RECORDING_STARTED') {
        finish({
          started: true,
          sidepanelOpened: !!event.data?.sidepanelOpened,
          fallbackReason: event.data?.fallbackReason,
          fallbackMessage: event.data?.fallbackMessage,
        })
      } else if (event.data?.type === 'SCREENATA_RECORDING_FAILED') {
        finish({
          started: false,
          reason: 'failed',
          fallbackReason: event.data?.fallbackReason,
          fallbackMessage: event.data?.fallbackMessage,
          error: event.data?.error,
        })
      }
    }

    window.addEventListener('message', handleAck)
    window.postMessage(
      {
        type: 'SCREENATA_START_RECORDING',
        organizationId,
        workspaceId,
        taskId,
        remediationTicketId,
        targetUrl,
        agentGuidance,
        contextLabel,
        completionMode: 'remedy',
        recordingMode: 'compliance',
        source,
      },
      window.location.origin
    )

    setTimeout(() => finish({ started: false, reason: 'no-ack' }), timeoutMs)
  })
}

/**
 * First checks if extension is installed, then guides user to open sidepanel
 * Since Chrome's sidepanel.open() requires a user gesture, we instruct users to manually open the extension
 * @returns Promise<{installed: boolean, message: string}>
 */
export async function handleStartRecording(): Promise<{
  installed: boolean;
  message: string;
}> {
  // Check if extension is installed
  const installed = await isExtensionInstalled();

  if (!installed) {
    // Extension not installed - open Chrome Web Store
    window.open(
      `https://chromewebstore.google.com/detail/deepguide-recorder-screen/${EXTENSION_ID}`,
      '_blank'
    );
    return {
      installed: false,
      message: 'Please install the DeepGuide extension to start recording.',
    };
  }

  // Extension is installed - ask user to open sidepanel
  return {
    installed: true,
    message: 'Please open the DeepGuide extension by clicking the extension icon in your browser toolbar to start recording.',
  };
}
